<script lang="ts" setup>
import { computed, defineProps, ref } from "vue";
import OpenChatButton from "@/components/controls/OpenChatButton.vue";
import PhoneNumber from "@/components/PhoneNumber.vue";
import LanguageTag from "@/components/status/LanguageTag.vue";
import type { TripMonitorRoute, TripMonitorTruck } from "@/api/trips/dto/trip-monitor";
import { TruckType } from "@/data/truck";
import { RejectedRouteStatuses, RouteStatus } from "@/data/trip";
import PlannedChip from "@/components/chips/PlannedChip.vue";
import { useI18n } from "vue-i18n";
import TruckRoutesPopover from "@/components/truck/TruckRoutesPopover.vue";
import { useHighlight } from "@/composables/useHighlight";

const { t } = useI18n();
const { highlight } = useHighlight();

const props = defineProps<{
  truck: TripMonitorTruck | null;
  route: TripMonitorRoute;
  truckRoutes: TripMonitorRoute[];
}>();

const showRoutesPopover = ref<boolean>(false);

const isDriverContactPerson = computed(
  () =>
    props.truck?.driver?.contactPersonId != null && props.truck?.driver?.contactPersonId === props.truck?.driver?.id,
);

const driverLanguage = computed(() => props.truck?.driver?.language);

const truckType = computed(() => {
  return TruckType.find((truck) => truck.id === props.truck?.type)?.name;
});

const isPlanned = computed(() => {
  return props.route.status === RouteStatus.planned;
});

const showViewAllRoutes = computed(() => {
  return props.truckRoutes.filter((route) => !RejectedRouteStatuses.includes(route.status)).length > 1;
});

function scrollToRoute(routeId: number | undefined) {
  if (!routeId) {
    return;
  }

  const routeHTML = document.querySelector(`[data-route-id="${routeId}"]`) as HTMLElement;

  routeHTML?.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });

  highlight(routeHTML, "trip__route--highlighted");

  showRoutesPopover.value = false;
}

function scrollToActiveRoute() {
  const activeRoute = props.truckRoutes.find((route) => route.status === RouteStatus.inProgress);

  if (!activeRoute) {
    return;
  }

  scrollToRoute(activeRoute.id);
}
</script>

<template>
  <div>
    <div v-if="isPlanned" class="d-flex align-center mb-3">
      <planned-chip />
      <v-btn
        class="ml-3 px-2"
        color="primary"
        density="comfortable"
        size="small"
        variant="outlined"
        @click="scrollToActiveRoute"
        >{{ t("trip.scroll-to-active-route") }}</v-btn
      >
    </div>

    <div class="d-flex align-center">
      <v-icon class="mr-1" color="primary" icon="mdi-truck-outline" size="20"></v-icon>
      <router-link
        :to="{ name: 'truck-view', params: { id: props.truck?.id } }"
        class="text-decoration-none text-primary font-weight-500"
        data-qa="truck-number"
        target="_blank"
      >
        {{ props.truck?.number }}
      </router-link>

      <template v-if="showViewAllRoutes">
        <truck-routes-popover
          v-model="showRoutesPopover"
          :current-route-id="props.route.id"
          :truck-id="props.truck?.id"
          location="top"
        >
          <template #activator="popoverProps">
            <v-btn
              class="ml-3 px-2 text-decoration-underline"
              color="primary"
              density="comfortable"
              size="small"
              v-bind="popoverProps"
              variant="text"
              >{{ t("trip.view-all-routes") }}</v-btn
            >
          </template>

          <template #actions="routeProps">
            <div class="d-flex align-center col-gap-2">
              <v-btn
                v-if="props.route.id !== routeProps.id"
                class="ml-3 px-2 text-uppercase font-weight-600 font-size-12"
                color="primary"
                density="comfortable"
                variant="outlined"
                @click="scrollToRoute(routeProps.id)"
                >{{ t("trip.scroll-to-route") }}
              </v-btn>

              <v-btn
                v-if="routeProps?.tripId"
                :to="{ name: 'trip-view', params: { id: routeProps.tripId } }"
                class="ml-3 px-2 text-uppercase font-weight-600 font-size-12"
                color="primary"
                density="comfortable"
                target="_blank"
                variant="outlined"
                >{{ t("trip.view-trip") }}
              </v-btn>
            </div>
          </template>
        </truck-routes-popover>
      </template>
    </div>

    <div class="mb-1">
      <span class="text-grey-darken-1 font-size-11" data-qa="truck-type">{{ truckType }}</span>
    </div>
    <div class="d-flex align-center mb-1" data-qa="driver-name">
      <router-link
        :to="{ name: 'user-view', params: { id: props.truck?.driver?.id } }"
        class="text-decoration-none text-primary font-weight-500 font-size-12"
        target="_blank"
      >
        {{ props.truck?.driver?.name }}
      </router-link>
      <span v-if="isDriverContactPerson" class="currency-icon ml-1" />
      <open-chat-button :user="props.truck?.driver" class="ml-2"></open-chat-button>
    </div>
    <div class="d-flex align-center">
      <phone-number
        v-if="props.truck?.driver?.phone"
        :number="props.truck.driver.phone"
        class="font-size-11 text-grey-darken-3"
      ></phone-number>
      <language-tag v-if="driverLanguage" :language="driverLanguage" class="ml-2" light></language-tag>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
